
import { useState } from "react";
import { base44 } from "@/api/base44Client";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { BarChart, Download, FileText, Calendar, CheckCircle, Clock, XCircle } from "lucide-react";
import { format, isValid } from "date-fns";

// Return a formatted date, or '-' when the value is missing/invalid.
// Kept local to this file per the "one file only" constraint.
const safeFormatDate = (value, fmt = "MMM d, yyyy") => {
  if (value === undefined || value === null || value === "") return "-";
  try {
    const d = value instanceof Date ? value : new Date(value);
    if (!isValid(d)) return "-";
    return format(d, fmt);
  } catch {
    return "-";
  }
};

export default function Reports() {
  const { data: closeouts = [], isLoading } = useQuery({
    queryKey: ['closeouts'],
    queryFn: () => base44.entities.Closeout.list('-closed_at')
  });

  const { data: rosterEntries = [] } = useQuery({
    queryKey: ['rosterEntries'],
    queryFn: () => base44.entities.RosterEntry.list()
  });

  const { data: excusals = [] } = useQuery({
    queryKey: ['excusals'],
    queryFn: () => base44.entities.ExcusalSubmission.list('-created_date')
  });

  // Count unique EA students with priority: P > R > A > E > Pending
  const getEACounts = () => {
    const studentMap = new Map();
    const eaEntries = rosterEntries.filter((e) => e.status === 'active' || e.status === 'excused');
    
    eaEntries.forEach(entry => {
      const otherId = entry.other_id;
      const currentStatus = studentMap.get(otherId);
      
      let entryStatus;
      if (entry.status === 'excused') {
        entryStatus = 'excused';
      } else {
        entryStatus = entry.attendance_status || 'pending';
      }
      
      // Priority: present > refusal > absent > excused > pending
      // P and R take precedence over E
      const priority = {
        'present': 5,
        'refusal': 4,
        'absent': 3,
        'excused': 2,
        'pending': 1
      };
      
      if (!currentStatus || priority[entryStatus] > priority[currentStatus]) {
        studentMap.set(otherId, entryStatus);
      }
    });
    
    let presentCount = 0;
    let excusedCount = 0;
    let absentCount = 0;
    let pendingCount = 0;
    let refusalCount = 0;
    
    studentMap.forEach(status => {
      if (status === 'present') presentCount++;
      else if (status === 'excused') excusedCount++;
      else if (status === 'absent') absentCount++;
      else if (status === 'refusal') refusalCount++;
      else pendingCount++;
    });
    
    return {
      assigned: studentMap.size,
      present: presentCount,
      excused: excusedCount,
      absent: absentCount,
      pending: pendingCount,
      refusal: refusalCount
    };
  };

  const eaCounts = getEACounts();
  const currentEAPresent = eaCounts.present;
  const currentEAExcused = eaCounts.excused;
  const currentEAAbsent = eaCounts.absent;
  const currentEAPending = eaCounts.pending;
  const currentEARefusal = eaCounts.refusal;

  // Current ASD counts (UNIQUE STUDENTS ONLY)
  const asdEntries = rosterEntries.filter((e) => e.status === 'asd');
  const currentASDPresentStudents = new Set(asdEntries.filter((e) => e.attendance_status === 'present').map((e) => e.other_id));
  const currentASDAbsentStudents = new Set(asdEntries.filter((e) => e.attendance_status === 'absent').map((e) => e.other_id));
  const currentASDExcusedStudents = new Set(asdEntries.filter((e) => e.attendance_status === 'excused').map((e) => e.other_id));
  const currentASDPendingStudents = new Set(asdEntries.filter((e) => e.attendance_status === 'pending').map((e) => e.other_id));
  
  const currentASDPresent = currentASDPresentStudents.size;
  const currentASDAbsent = currentASDAbsentStudents.size;
  const currentASDExcused = currentASDExcusedStudents.size;
  const currentASDPending = currentASDPendingStudents.size;

  const downloadEARoster = () => {
    const activeEA = rosterEntries.filter((e) => e.status === 'active'); // Re-filter for download scope
    const headers = [
      'Attendance', 'Campus', 'Student Last Name', 'Student First Name', 'Other ID',
      'Student ID', 'Method of Instruction', 'Grade Level', 'Course', 'Section',
      'Course Short Description', 'Teacher', 'Assignment', 'Assignment Grade',
      'Athletic Grade', 'Comments', 'Date', 'Time', 'F1G1 Parent Phone',
      'F1G2 Parent Phone', 'Student Email', 'F1G1 Parent Email',
      'F2G1 Parent Phone', 'F2G1 Parent Email'
    ];

    const rows = activeEA.map((e) => [
      e.attendance_status, e.campus, e.student_last_name, e.student_first_name,
      e.other_id, e.student_id, e.method_of_instruction, e.grade_level,
      e.course, e.section, e.course_short_description, e.teacher,
      e.assignment, e.assignment_grade, e.athletic_grade, e.comments,
      e.date, e.time, e.f1g1_parent_phone, e.f1g2_parent_phone,
      e.student_email, e.f1g1_parent_email, e.f2g1_parent_phone, e.f2g1_parent_email
    ]);

    const csv = [headers, ...rows].map((row) => row.map((cell) => `"${cell || ''}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `EA_Roster_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
  };

  const downloadASDRoster = () => {
    const asdEntries = rosterEntries.filter((e) => e.status === 'asd'); // Re-filter for download scope
    const headers = [
      'Attendance', 'Campus', 'Student Last Name', 'Student First Name', 'Other ID',
      'Student ID', 'Method of Instruction', 'Grade Level', 'Course', 'Section',
      'Course Short Description', 'Teacher', 'Assignment', 'Assignment Grade',
      'Athletic Grade', 'Comments', 'Date', 'Time', 'F1G1 Parent Phone',
      'F1G2 Parent Phone', 'Student Email', 'F1G1 Parent Email',
      'F2G1 Parent Phone', 'F2G1 Parent Email'
    ];

    const rows = asdEntries.map((e) => [
      e.attendance_status, e.campus, e.student_last_name, e.student_first_name,
      e.other_id, e.student_id, e.method_of_instruction, e.grade_level,
      e.course, e.section, e.course_short_description, e.teacher,
      e.assignment, e.assignment_grade, e.athletic_grade, e.comments,
      e.date, e.time, e.f1g1_parent_phone, e.f1g2_parent_phone,
      e.student_email, e.f1g1_parent_email, e.f2g1_parent_phone, e.f2g1_parent_email
    ]);

    const csv = [headers, ...rows].map((row) => row.map((cell) => `"${cell || ''}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ASD_Roster_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
  };

  const downloadExcusals = () => {
    const headers = [
      'Submission Date', 'Status', 'Student Name', 'Other ID', 'Student Email',
      'Teacher Name', 'Teacher Email', 'Subject', 'Reason',
      'Reviewed Date', 'Teacher Notes', 'Proof URL'
    ];

    const rows = excusals.map((e) => [
      e.submission_date || '',
      e.status || '',
      e.student_name || '',
      e.other_id || '',
      e.student_email || '',
      e.teacher_name || '',
      e.teacher_email || '',
      e.subject || '',
      e.reason || '',
      e.reviewed_date || '',
      e.teacher_notes || '',
      e.proof_of_completion_url || ''
    ]);

    const csv = [headers, ...rows].map((row) => row.map((cell) => `"${cell || ''}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Excusal_Requests_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
  };

  const downloadCloseoutsHistory = () => {
    const headers = [
      'Date', 'Time',
      'EA Present', 'EA Excused', 'EA Absent', 'EA Refusal', 'EA PEA',
      'ASD Present', 'ASD Excused', 'ASD Absent', 'ASD Refusal', 'ASD PEA',
      'Kind', 'Closed By'
    ];

    const rows = closeouts.map((c) => [
      c.date || '', safeFormatDate(c.closed_at, 'HH:mm'),
      c.eaPresent || 0, c.eaExcused || 0, c.eaAbsent || 0, c.eaRefusal || 0, c.eaPEA || 0,
      c.asdPresent || 0, c.asdExcused || 0, c.asdAbsent || 0, c.asdRefusal || 0, c.asdPEA || 0,
      c.kind || '', c.closed_by || ''
    ]);

    const csv = [headers, ...rows].map((row) => row.map((cell) => `"${cell || ''}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Closeout_History_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
  };

  const downloadCloseoutSnapshot = (closeout) => {
    if (!closeout.roster_snapshot) {
      alert('No snapshot data available for this closeout');
      return;
    }

    try {
      const snapshot = JSON.parse(closeout.roster_snapshot);
      const eaRoster = snapshot.ea_roster || [];
      const asdRoster = snapshot.asd_roster || [];

      const headers = [
        'Roster Type', 'Attendance', 'Campus', 'Student Last Name', 'Student First Name', 'Other ID',
        'Student ID', 'Method of Instruction', 'Grade Level', 'Course', 'Section',
        'Course Short Description', 'Teacher', 'Assignment', 'Assignment Grade',
        'Athletic Grade', 'Comments', 'Date', 'Time', 'F1G1 Parent Phone',
        'F1G2 Parent Phone', 'Student Email', 'F1G1 Parent Email',
        'F2G1 Parent Phone', 'F2G1 Parent Email'
      ];

      const eaRows = eaRoster.map((e) => [
        'EA', e.attendance_status, e.campus, e.student_last_name, e.student_first_name,
        e.other_id, e.student_id, e.method_of_instruction, e.grade_level,
        e.course, e.section, e.course_short_description, e.teacher,
        e.assignment, e.assignment_grade, e.athletic_grade, e.comments,
        e.date, e.time, e.f1g1_parent_phone, e.f1g2_parent_phone,
        e.student_email, e.f1g1_parent_email, e.f2g1_parent_phone, e.f2g1_parent_email
      ]);

      const asdRows = asdRoster.map((e) => [
        'ASD', e.attendance_status, e.campus, e.student_last_name, e.student_first_name,
        e.other_id, e.student_id, e.method_of_instruction, e.grade_level,
        e.course, e.section, e.course_short_description, e.teacher,
        e.assignment, e.assignment_grade, e.athletic_grade, e.comments,
        e.date, e.time, e.f1g1_parent_phone, e.f1g2_parent_phone,
        e.student_email, e.f1g1_parent_email, e.f2g1_parent_phone, e.f2g1_parent_email
      ]);

      const csv = [headers, ...eaRows, ...asdRows].map((row) => row.map((cell) => `"${cell || ''}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Closeout_Snapshot_${closeout.date || 'unknown'}.csv`;
      a.click();
    } catch (err) {
      alert('Error downloading snapshot: ' + err.message);
    }
  };

  const pendingExcusals = excusals.filter(e => e.status === 'pending').length;
  const approvedExcusals = excusals.filter(e => e.status === 'approved').length;
  const deniedExcusals = excusals.filter(e => e.status === 'denied').length;

  return (
    <div className="p-4 md:p-8 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Reports & Summary</h1>
            <p className="text-slate-600 mt-1">EA/ASD PEAR Numbers for District</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={downloadEARoster} className="bg-red-700 hover:bg-red-800">
              <Download className="w-4 h-4 mr-2" />
              Download EA CSV
            </Button>
            <Button onClick={downloadASDRoster} className="bg-red-800 hover:bg-red-900">
              <Download className="w-4 h-4 mr-2" />
              Download ASD CSV
            </Button>
          </div>
        </div>

        {/* Current Cycle Summary */}
        <Card className="border-none shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart className="w-5 h-5" />
              Current EA Cycle (Live)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
              <div className="p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-700 font-medium">Assigned</p>
                <p className="text-3xl font-bold text-blue-900">{eaCounts.assigned}</p>
              </div>
              <div className="p-4 bg-green-50 rounded-lg">
                <p className="text-sm text-green-700 font-medium">Present</p>
                <p className="text-3xl font-bold text-green-900">{currentEAPresent}</p>
              </div>
              <div className="p-4 bg-orange-50 rounded-lg">
                <p className="text-sm text-orange-700 font-medium">Excused</p>
                <p className="text-3xl font-bold text-orange-900">{currentEAExcused}</p>
              </div>
              <div className="p-4 bg-yellow-50 rounded-lg">
                <p className="text-sm text-yellow-700 font-medium">Absent</p>
                <p className="text-3xl font-bold text-yellow-900">{currentEAAbsent}</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-700 font-medium">Pending</p>
                <p className="text-3xl font-bold text-slate-900">{currentEAPending}</p>
              </div>
              <div className="p-4 bg-red-50 rounded-lg">
                <p className="text-sm text-red-700 font-medium">Refusal</p>
                <p className="text-3xl font-bold text-red-900">{currentEARefusal}</p>
              </div>
            </div>
            <div className="mt-4 p-4 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-700">
                <strong>EA PEA:</strong> {currentEAPresent + currentEAExcused + currentEAAbsent} | 
                <strong> Total EA Students:</strong> {eaCounts.assigned}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Current ASD Summary */}
        <Card className="border-none shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart className="w-5 h-5" />
              Current ASD Roster (Live)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-700 font-medium">Assigned</p>
                <p className="text-3xl font-bold text-blue-900">{currentASDPresent + currentASDAbsent + currentASDExcused + currentASDPending}</p>
              </div>
              <div className="p-4 bg-green-50 rounded-lg">
                <p className="text-sm text-green-700 font-medium">Present</p>
                <p className="text-3xl font-bold text-green-900">{currentASDPresent}</p>
              </div>
              <div className="p-4 bg-yellow-50 rounded-lg">
                <p className="text-sm text-yellow-700 font-medium">Absent</p>
                <p className="text-3xl font-bold text-yellow-900">{currentASDAbsent}</p>
              </div>
              <div className="p-4 bg-orange-50 rounded-lg" data-testid="asd-excused-card">
                <p className="text-sm text-orange-700 font-medium">Excused</p>
                <p className="text-3xl font-bold text-orange-900">{currentASDExcused}</p>
              </div>
              <div className="p-4 bg-red-50 rounded-lg">
                <p className="text-sm text-red-700 font-medium">Pending</p>
                <p className="text-3xl font-bold text-red-900">{currentASDPending}</p>
              </div>
            </div>
            <div className="mt-4 p-4 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-700">
                <strong>ASD PEA:</strong> {currentASDPresent + currentASDAbsent + currentASDExcused} | 
                <strong> Total ASD Students:</strong> {currentASDPresent + currentASDAbsent + currentASDExcused + currentASDPending}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Excusal Requests Summary has moved to its own page under
            "Excusal Requests → Summary" in the sidebar. */}

        {/* Historical Closeouts */}
        <Card className="border-none shadow-lg">
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Historical Closeouts
              </CardTitle>
              {closeouts.length > 0 && (
                <Button onClick={downloadCloseoutsHistory} className="bg-red-700 hover:bg-red-800" size="sm">
                  <Download className="w-4 h-4 mr-2" />
                  Download History CSV
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Time</TableHead>
                    <TableHead colSpan={6} className="text-center bg-blue-50">EA Statistics</TableHead>
                    <TableHead colSpan={5} className="text-center bg-red-50">ASD Statistics</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                  <TableRow>
                    <TableHead></TableHead>
                    <TableHead></TableHead>
                    <TableHead className="text-xs">Assigned</TableHead>
                    <TableHead className="text-xs">P</TableHead>
                    <TableHead className="text-xs">E</TableHead>
                    <TableHead className="text-xs">A</TableHead>
                    <TableHead className="text-xs">R</TableHead>
                    <TableHead className="text-xs">PEA</TableHead>
                    <TableHead className="text-xs">Assigned</TableHead>
                    <TableHead className="text-xs">P</TableHead>
                    <TableHead className="text-xs">A</TableHead>
                    <TableHead className="text-xs">R</TableHead>
                    <TableHead className="text-xs">PEA</TableHead>
                    <TableHead className="text-xs">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={14} className="text-center py-8 text-slate-500">
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : closeouts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={14} className="text-center py-8 text-slate-500">
                        No closeouts yet. Complete your first EA cycle to see reports.
                      </TableCell>
                    </TableRow>
                  ) : (
                    closeouts.map((closeout) => (
                      <TableRow key={closeout.id} className="hover:bg-slate-50">
                        <TableCell className="font-medium">
                          {safeFormatDate(closeout.date)}
                        </TableCell>
                        <TableCell>{safeFormatDate(closeout.closed_at, 'HH:mm')}</TableCell>
                        <TableCell className="font-semibold">{closeout.eaPEA || 0}</TableCell>
                        <TableCell>
                          <Badge className="bg-green-100 text-green-800">
                            {closeout.eaPresent || 0}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className="bg-orange-100 text-orange-800">
                            {closeout.eaExcused || 0}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className="bg-yellow-100 text-yellow-800">
                            {closeout.eaAbsent || 0}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className="bg-red-100 text-red-800">
                            {closeout.eaRefusal || 0}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-semibold text-blue-700">{closeout.eaPEA || 0}</TableCell>
                        <TableCell className="font-semibold">{closeout.asdPEA || 0}</TableCell>
                        <TableCell>
                          <Badge className="bg-green-100 text-green-800">
                            {closeout.asdPresent || 0}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className="bg-yellow-100 text-yellow-800">
                            {closeout.asdAbsent || 0}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className="bg-red-100 text-red-800">
                            {closeout.asdRefusal || 0}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-semibold text-red-700">{closeout.asdPEA || 0}</TableCell>
                        <TableCell>
                          <Button
                            onClick={() => downloadCloseoutSnapshot(closeout)}
                            variant="ghost"
                            size="sm"
                            className="text-red-700 hover:text-red-800"
                          >
                            <Download className="w-4 h-4 mr-1" />
                            Snapshot
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
